import { PlusIcon } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';

import { ITableHeader, Table, TableSortOrder } from 'components/table/Table';
import { Pagination } from 'components/pagination/Pagination';
import { Pill } from 'components/pill/Pill';
import { SimpleSelect } from 'components/select/SimpleSelect';
import { LinkButton } from 'components/button/ButtonLink';
import { formatDate } from '@utils/date';
import { invariant } from '@utils/invariant';
import {
  fetchTrailerChecks,
  type TrailerCheckStatusType,
  type TrailerCheckSortBy,
  type TrailerCheckEventTypeType,
} from '../models';

const TABLE_HEADERS: ITableHeader[] = [
  { id: 'eventDate', name: 'Datum', sortable: true },
  { id: 'eventType', name: 'Type', sortable: true },
  { id: 'driver', name: 'Chauffeur' },
  { id: 'status', name: 'Status', sortable: true },
  { id: 'cannotContinue', name: 'Doorrijden', sortable: true },
  { id: 'isActive', name: 'Actief' },
  { id: 'categories', name: 'Categorieën' },
  { id: 'photos', name: "Foto's" },
  { id: 'location', name: 'Locatie' },
  { id: 'resolved', name: 'Opgelost', sortable: true },
];

const DAMAGE_CATEGORY_LABELS: Record<string, string> = {
  Tires: 'Banden',
  Brakes: 'Remmen',
  Lights: 'Verlichting',
  Body: 'Carrosserie',
  Floor: 'Vloer',
  Tarpaulin: 'Zeil',
  Doors: 'Deuren',
  Coupling: 'Koppeling',
  Other: 'Overig',
};

const EVENT_TYPE_LABELS: Record<TrailerCheckEventTypeType, string> = {
  PickedUp: 'Opgehaald',
  DroppedOff: 'Afgezet',
};

const STATUS_FILTER_OPTIONS = [
  { key: 'OK', name: 'OK' },
  { key: 'Damaged', name: 'Beschadigd' },
];

const RESOLVED_FILTER_OPTIONS = [
  { key: 'true', name: 'Opgelost' },
  { key: 'false', name: 'Niet opgelost' },
];

const PAGE_SIZE = 50;

export const TrailerChecksPage = () => {
  const { trailerId } = useParams<{ trailerId: string }>();
  invariant(trailerId);

  const [statusFilter, setStatusFilter] = useState<{ key: string; name: string } | null>(null);
  const [resolvedFilter, setResolvedFilter] = useState<{ key: string; name: string } | null>(null);
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<TrailerCheckSortBy | undefined>('eventDate');
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>('desc');

  const { data, error, isLoading, mutate } = useSWR(
    ['trailer-checks', trailerId, statusFilter?.key, resolvedFilter?.key, sortBy, sortOrder, offset],
    () =>
      fetchTrailerChecks({
        trailerId: +trailerId,
        status: (statusFilter?.key as TrailerCheckStatusType) || undefined,
        resolved: resolvedFilter ? resolvedFilter.key === 'true' : undefined,
        sortBy,
        sortOrder,
        limit: PAGE_SIZE + 1,
        offset,
      }),
    { keepPreviousData: true },
  );

  const hasNext = (data?.length ?? 0) > PAGE_SIZE;
  const checks = useMemo(() => (data ?? []).slice(0, PAGE_SIZE), [data]);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
          Nieuwe check
        </LinkButton>
      </div>

      <div className="flex flex-wrap items-end mb-4 gap-4">
        <div className="min-w-48">
          <label>
            <div className="label-text">Status</div>
            <SimpleSelect
              selectedItem={statusFilter}
              items={STATUS_FILTER_OPTIONS}
              onSelect={(item) => {
                setStatusFilter(item);
                setOffset(0);
              }}
              isOptional={true}
            />
          </label>
        </div>

        <div className="min-w-48">
          <label>
            <div className="label-text">Opgelost</div>
            <SimpleSelect
              selectedItem={resolvedFilter}
              items={RESOLVED_FILTER_OPTIONS}
              onSelect={(item) => {
                setResolvedFilter(item);
                setOffset(0);
              }}
              isOptional={true}
            />
          </label>
        </div>
      </div>

      <Table
        idKey="id"
        headers={TABLE_HEADERS}
        data={checks}
        error={error}
        onRetry={() => mutate()}
        isLoading={isLoading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={(id, order) => {
          setSortBy(id as TrailerCheckSortBy | undefined);
          setSortOrder(order);
          setOffset(0);
        }}
        mapData={(check) => {
          const statusColor = check.cannotContinue ? 'negative' : check.status === 'Damaged' ? 'primary' : 'positive';
          const statusLabel = check.status === 'OK' ? 'OK' : 'Beschadigd';

          return [
            <Link to={check.id} className="link-text">
              {formatDate(check.eventDate)}
            </Link>,
            EVENT_TYPE_LABELS[check.eventType],
            check.driverName,
            <Pill color={statusColor} size="sm">
              {statusLabel}
            </Pill>,
            check.cannotContinue ? (
              <Pill color="negative" size="sm">
                Niet mogelijk
              </Pill>
            ) : (
              <span className="text-fg-muted">Mogelijk</span>
            ),
            check.isActive ? 'Ja' : 'Nee',
            check.damageCategories.map((c) => DAMAGE_CATEGORY_LABELS[c] ?? c).join(', ') || '-',
            check.photoCount,
            check.locationName || '-',
            check.resolved ? (
              <Pill color="positive" size="sm">
                Ja
              </Pill>
            ) : check.status === 'Damaged' ? (
              <Pill color="primary" size="sm">
                Nee
              </Pill>
            ) : (
              '-'
            ),
          ];
        }}
      />

      <div className="my-4">
        <Pagination
          hasPrevious={offset > 0}
          previous={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          hasNext={hasNext}
          next={() => setOffset(offset + PAGE_SIZE)}
          isFetching={isLoading}
        />
      </div>
    </div>
  );
};
